import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/exchange`;

// Context for exchange auth
const ExchangeAuthContext = createContext(null);

export const useExchangeAuth = () => {
  const context = useContext(ExchangeAuthContext);
  if (!context) {
    throw new Error('useExchangeAuth must be used within ExchangeAuthProvider');
  }
  return context;
};

export const ExchangeAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('exchange_token'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data.user);
        setOrganization(response.data.organization);
      } catch (error) {
        console.error('Failed to load user:', error);
        localStorage.removeItem('exchange_token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user_id, role, organization_id } = response.data;
    
    localStorage.setItem('exchange_token', newToken);
    setToken(newToken);
    
    // Reload user data
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setUser(userResponse.data.user);
    setOrganization(userResponse.data.organization);
    
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user_id } = response.data;
    
    localStorage.setItem('exchange_token', newToken);
    setToken(newToken);
    
    // Reload user data
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setUser(userResponse.data.user);
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('exchange_token');
    setToken(null);
    setUser(null);
    setOrganization(null);
  };

  const value = {
    user,
    organization,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser: async () => {
      if (token) {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data.user);
        setOrganization(response.data.organization);
      }
    }
  };

  return (
    <ExchangeAuthContext.Provider value={value}>
      {children}
    </ExchangeAuthContext.Provider>
  );
};

// API helper functions
export const exchangeApi = {
  // Organizations
  createOrganization: (data) => axios.post(`${API}/organizations`, data),
  getOrganization: (id) => axios.get(`${API}/organizations/${id}`),
  searchOrganizations: (params) => axios.get(`${API}/organizations/search`, { params }),
  
  // Credentials - File Upload
  uploadCredentialFile: (formData) => axios.post(`${API}/credentials/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadCredential: (data) => axios.post(`${API}/credentials`, data),
  getCredentials: () => axios.get(`${API}/credentials`),
  getCredential: (id) => axios.get(`${API}/credentials/${id}`),
  downloadCredentialFile: (id) => axios.get(`${API}/credentials/${id}/download`, { responseType: 'blob' }),
  verifyCredential: (id) => axios.get(`${API}/credentials/${id}/verify`),
  getVerificationCertificate: (id) => axios.get(`${API}/credentials/${id}/certificate`),
  suggestCredentialType: (filename) => axios.post(`${API}/credentials/suggest-type`, null, { params: { filename } }),
  
  // Credential Categories
  getCredentialCategories: (role) => role 
    ? axios.get(`${API}/credential-categories/${role}`)
    : axios.get(`${API}/credential-categories`),
  getRequiredCredentialsForRole: (role, state) => axios.get(`${API}/credential-categories/${role}`, { params: { state } }),
  
  // Requests
  createRequest: (data) => axios.post(`${API}/requests`, data),
  getIncomingRequests: () => axios.get(`${API}/requests/incoming`),
  getOutgoingRequests: () => axios.get(`${API}/requests/outgoing`),
  approveRequest: (id, data) => axios.post(`${API}/requests/${id}/approve`, data),
  denyRequest: (id, reason) => axios.post(`${API}/requests/${id}/deny`, null, { params: { reason } }),
  downloadCredentials: (id, token) => axios.get(`${API}/requests/${id}/download`, { params: { token } }),
  
  // Trust relationships
  createTrust: (data) => axios.post(`${API}/trust`, data),
  getTrustRelationships: () => axios.get(`${API}/trust`),
  deleteTrust: (id) => axios.delete(`${API}/trust/${id}`),
  
  // Notifications
  updateNotificationPrefs: (prefs) => axios.put(`${API}/notifications/preferences`, prefs),
  getNotificationHistory: (limit = 20) => axios.get(`${API}/notifications/history`, { params: { limit } }),
  
  // Blockchain
  verifyDocumentHash: (hash) => axios.get(`${API}/blockchain/verify/${hash}`),
  getBlockchainHistory: (credentialId) => axios.get(`${API}/blockchain/history/${credentialId}`),
  
  // Utility
  getPricing: () => axios.get(`${API}/pricing`),
  getStateRules: (state) => state ? axios.get(`${API}/state-rules`, { params: { state } }) : axios.get(`${API}/state-rules`),
  getRequiredCredentials: (state, context = 'provider') => axios.get(`${API}/state-rules/${state}/required`, { params: { context } }),
  getStats: () => axios.get(`${API}/stats`),
  getAuditLog: (limit = 50, action = null) => axios.get(`${API}/audit-log`, { params: { limit, action } })
};

export default ExchangeAuthProvider;
